import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";

import DocumentViewModal from "../../../../../../Components/DocumentViewModal";
import TenderDocumentModal from "../TenderDocumentModal";
import NoDataCreated from "../../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../../Components/ErrorPage";
import { goToTop } from "../../../../../../Components/goToTop";

import { useAppDispatch, useAppSelector } from "../../../../../../Controllers/hooks";
import { ProjectTenderDetailInterface, TenderDocumentInterface } from "../../../../../../Controllers/Types/Tender/projectTenderDetailsInterface";
import { setTenderDocumentListForView } from "../../../../../../Controllers/Slices/tenderSlice";
import { formatDateString } from "../../../../../../Utils/getDate";
import { baseURL } from "../../../../../../Api/ApiConstant";
import { checkFileOthers, checkText } from "../../../../../../Utils/Validator";
import { addTenderDocumentApi, deleteTenderDocumentApi, updateTenderDocumentApi } from "../../../../../../Api/tenderApi";
import { getProjectTenderDetailList } from "../../../../../../Controllers/Thunks/tenderThunks";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../Controllers/Store";
import { hasPermission } from "../../../../../../Controllers/Selectors/permissionsSelector";

const TenderDocument: React.FC = () => {
    const serviceName = 'tender documents';
    const loadingPage = '';
    const error = '';

    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch()
    const location = useLocation();
    const projectTenderDetailsInterface: ProjectTenderDetailInterface = location.state.tenderDocument as ProjectTenderDetailInterface

    const projectData: any = location?.state.projectData as any;


    const toast = useRef<Toast>(null);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
    const [selectedTenderDocumentData, setSelectedTenderDocumentData] = useState<TenderDocumentInterface | null>(null);


    /* ****************************** Form variables **********************************/
    const [documentName, setDocumentName] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');

    /* Error message */
    const [documentNameError, setDocumentNameError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');
    /* **************************************************************************** */

    const { loadingProjectTenderDetail, tenderDocumentListForView } = useAppSelector((state) => state.tender);


    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermission_add_tender_document = useCanEdit('Tender', 'add_tender_document');
    const isPermission_tender_document_edit = useCanEdit('Tender', 'tender_document_edit');
    const isPermission_download_tender_document = useCanEdit('Tender', 'download_tender_document');

    useEffect(() => {
        dispatch(setTenderDocumentListForView(projectTenderDetailsInterface!))
    }, []);


    /* ***************************** For tender document ***************************** */
    const handleCloseDocumentModal = () => {
        setShowDocumentModal(false);
    }

    const handleAddDocument = () => {
        setShowDocumentModal(true);
        setDataState('Add');

        setDocumentName('');
        setFile(null);
        setFileName('');
    }


    function checkCredentialsUploadFile(isRequiredFile: boolean): boolean {
        const isServiceNameValid = checkText(documentName, true, setDocumentNameError);
        const isUploadedFileValid = checkFileOthers(file!, isRequiredFile, setFileError);

        return isServiceNameValid && isUploadedFileValid;
    }



    const handleCreateDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsUploadFile(true)) {
            setLoading(true);

            const res = await addTenderDocumentApi(projectTenderDetailsInterface?.project_tender_part_id!, documentName, file!)

            setLoading(false);

            if (res.status) {
                dispatch(getProjectTenderDetailList(projectTenderDetailsInterface.folder_id));

                setSuccessMessage('Document added successfully.');
                setShowAlert(true);

                setTimeout(() => {
                    setShowAlert(false);
                }, 2000);

                handleCloseDocumentModal();

            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating document',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleEditDocument = (data: TenderDocumentInterface) => {
        setShowDocumentModal(true);
        setDataState('Edit');
        setSelectedTenderDocumentData(data);

        setDocumentName(data?.document_name);
    }

    const handleUpdateDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsUploadFile(false)) {
            setLoading(true);

            const res = await updateTenderDocumentApi(selectedTenderDocumentData?.id!, documentName, file!)

            setLoading(false);

            if (res.status) {
                dispatch(getProjectTenderDetailList(projectTenderDetailsInterface.folder_id));

                setSuccessMessage('Document updated successfully.');
                setShowAlert(true);

                setTimeout(() => {
                    setShowAlert(false);
                }, 2000);

                handleCloseDocumentModal();

            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating document',
                        life: 3000
                    });
                }
            }
        }


    }

    const handleDeleteData = (dataId: number): void => {
        confirmDialog({
            message: 'Are you sure you want to delete the document?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteData(dataId),
        });
    }

    const deleteData = async (dataId: number) => {
        const res = await deleteTenderDocumentApi(dataId)
        if (res.status) {
            setShowAlert(true);
            setSuccessMessage('Document deleted successfully.');
            setTimeout(() => {
                setShowAlert(false);
            }, 1000);

            dispatch(getProjectTenderDetailList(projectTenderDetailsInterface.folder_id));


        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting document.',
                    life: 3000
                });
            }
        }

    }

    /* for file upload */
    const handleFileUpload = (file: File) => {
        setFile(file);
        setFileName(file.name);
    };

    const handleClearFile = () => {
        setFile(null);
        setFileName('');
    };
    /* *************************************************************************** */


    /* ***************************** For table view ****************************** */
    /* Table structure */
    const editDataBody = (rowData: TenderDocumentInterface) => {
        const data = rowData;
        return (
            <button className="data_action_btn primary_table_btn p-ripple"
                onClick={() => handleEditDocument(data)}
                disabled={!isPermission_tender_document_edit}>
                <i className="bi bi-pencil-square"></i>
                <Ripple />
            </button>
        )
    }

    const deleteDataBody = (rowData: TenderDocumentInterface) => {
        const dataId = rowData.id;
        return (
            <button className="data_action_btn danger_table_btn p-ripple"
                onClick={() => handleDeleteData(dataId)}>
                <i className="bi bi-trash3"></i>
                <Ripple />
            </button>
        )
    }

    const viewDocumentBody = (rowData: TenderDocumentInterface) => {
        const filePath = baseURL + rowData.document;
        return (
            <div className="tabel_btn_area mt-1 mb-1">
                <Button label="View" className="tabel_btn view_btn" disabled={!filePath} onClick={() => handleViewDocument(filePath!)}
                    tooltip={`${!filePath ? 'File not exist!' : 'Click to view document'}`}
                    tooltipOptions={{ className: "btn_tooltip", position: 'top', showOnDisabled: true }} />
            </div>
        )
    };
    /* *************************************************************************** */


    /* ***************************** For view file ****************************** */
    const handleViewDocument = (document: string) => {
        setFileUrl(document);
        setShowFileViewModal(true);
    }

    const handleCloseDocumentViewModal = () => {
        setShowFileViewModal(false);
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingProjectTenderDetail ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData?.job_number}&nbsp;{projectData?.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">{projectData?.folder}</li>
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData?.feature_name}
                                </li>
                                <li className="page_breadcrumb_item active">{serviceName}</li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-3">
                            {isPermission_add_tender_document && <div className="page_action_area">
                                <button className="add_data_link_btn p-ripple" onClick={handleAddDocument}>
                                    <i className="bi bi-plus-circle"></i>
                                    <span>Add document</span>
                                    <Ripple />
                                </button>
                            </div>}
                        </div>

                        <div className="page_main_content_section">
                            {tenderDocumentListForView?.length > 0 ? (
                                <DataTable
                                    value={tenderDocumentListForView}
                                    paginator
                                    size="small"
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                >
                                    <Column
                                        header="Document name"
                                        headerClassName="text_purple"
                                        field="document_name"
                                        body={(rowData: TenderDocumentInterface) => (
                                            <span className="text_bold">
                                                {rowData.document_name ? rowData.document_name : '---'}
                                            </span>
                                        )}
                                        style={{ width: '40%' }}
                                    ></Column>

                                    <Column
                                        header="Upload date"
                                        headerClassName="text_purple"
                                        alignHeader={'center'}
                                        field="updated_at"
                                        body={(rowData: TenderDocumentInterface) => (
                                            <span className="text_no_wrap flex_center">
                                                {rowData.updated_at ? formatDateString(rowData.updated_at) : '---'}
                                            </span>
                                        )}
                                        style={{ width: '20%' }}
                                    ></Column>

                                    <Column
                                        header="Edit"
                                        headerClassName="text_purple"
                                        body={editDataBody}
                                        alignHeader="center"
                                        className="data_action_btn_area"
                                        style={{ width: '10%' }}
                                    ></Column>

                                    <Column
                                        header="Delete"
                                        headerClassName="text_purple"
                                        body={deleteDataBody}
                                        alignHeader="center"
                                        className="data_action_btn_area"
                                        style={{ width: '10%' }}
                                    ></Column>

                                    <Column
                                        body={viewDocumentBody}
                                        className="table_btn_area"
                                        style={{ width: '20%' }}
                                    ></Column>

                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${serviceName} document added. To add the Document click on add button`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Tender document modal */}
            <TenderDocumentModal
                isOpen={showDocumentModal}
                onClose={handleCloseDocumentModal}
                dataState={dataState}
                serviceName={serviceName}
                toast={toast}
                documentName={documentName}
                setDocumentName={setDocumentName}
                uploadedFileName={fileName}
                handleFileUpload={handleFileUpload}
                handleClearFile={handleClearFile}
                loading={loading}
                documentNameError={documentNameError}
                fileError={fileError}
                isDocumentNameRequired={true}
                isFileRequired={dataState === 'Add' ? true : false}
                handleCreateDocument={handleCreateDocument}
                handleUpdateDocument={handleUpdateDocument}
            />


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={fileUrl!}
            />


            {/* Show success message */}
            <div className={`message_backdrop ${showAlert ? 'show' : ''}`} onClick={handleCloseAlert}>
                <div className={`message_box ${showAlert ? 'show' : ''}`} onClick={e => e.stopPropagation()}>
                    <div className="message_box_content">
                        <i className="bi bi-check-circle-fill message_box_icon"></i>
                        <h6 className="message_box_message">
                            {successMessage}
                        </h6>
                    </div>
                </div>
            </div>
            {/*  */}
        </React.Fragment>
    )
}

export default TenderDocument;
